import AddressForm from "@/common/components/AddressForm"
import useAddressForm from "@/common/hooks/useAddressForm";
import { AddressValidationSchema } from "@/common/schemas/address.schema"
import { AddressData } from "@/common/types/address"
import { RequestedReview } from "@/features/establishmentLeadsFunnel/components/RequestedReview"
import { ReviewButtons } from "@/features/establishmentLeadsFunnel/components/ReviewButtons"
import useReviewSectionLead from "@/features/establishmentLeadsFunnel/hooks/useReviewSectionLead"
import { EstablishmentLeadsFunnelUpdateValidationSchema } from "@/features/establishmentLeadsFunnel/schemas/update.schema"
import { EstablishmentLeadData } from "@/features/establishmentLeadsFunnel/types/establishmentLead.type"
import { useIsMutating } from "@tanstack/react-query"
import { FieldErrors, UseFormSetError, UseFormSetValue } from "react-hook-form"

interface AddressSectionProps {
  establishmentLeadId?: string
  address?: EstablishmentLeadData['address']
  leadState: EstablishmentLeadData['state']
  setValue?: UseFormSetValue<EstablishmentLeadsFunnelUpdateValidationSchema>
  setError?: UseFormSetError<EstablishmentLeadsFunnelUpdateValidationSchema>
  errors: FieldErrors<EstablishmentLeadsFunnelUpdateValidationSchema>
  submit?: () => void
  updateLeadOnSuccess?: () => void
  disabled?: boolean
}

export const AddressSection = ({
  establishmentLeadId,
  address,
  leadState,
  setValue,
  setError,
  errors,
  submit,
  updateLeadOnSuccess,
  disabled,
}: AddressSectionProps) => {
  const isMutating = !!useIsMutating();

  const addressForm = useAddressForm({
    initialAddress: address as AddressData,
    disabled,
    onAddressChange: (address, isValid) => {
      if (!disabled) {
        setValue?.("address_attributes", address)

        if (isValid) {
          submit?.()
        } else {
          setError?.("address_attributes.zipcode", { message: "CEP não encontrado" })
        }
      }
    },
  });

  const {
    mutate: approveAddressLead,
    isError: approveAddressLeadIsError,
  } = useReviewSectionLead({
    establishmentLeadId,
    state: 'approved',
    section: 'address_attributes',
    onSuccess: updateLeadOnSuccess,
  })

  const {
    mutate: declineAddressLead,
    isError: declineAddressLeadIsError,
  } = useReviewSectionLead({
    establishmentLeadId,
    state: 'disapproved',
    section: 'address_attributes',
    onSuccess: updateLeadOnSuccess,
  })

  const isServerError = (approveAddressLeadIsError || declineAddressLeadIsError)

  return (
    <div className="flex flex-col gap-4">
      {isServerError && (
        <p className="text-sm text-danger-soft">Ocorreu um erro no servidor, cheque os campos e tente novamente</p>
      )}
      <div className="flex justify-between">
        <p className="text-[18px] font-bold">Endereço</p>
        {(leadState === 'qualifying_review') && (
          <ReviewButtons
            name="address"
            reviewState={address?.last_review?.state}
            onApprove={approveAddressLead}
            onDecline={declineAddressLead}
            isPending={isMutating}
          />
        )}
      </div>

      {address?.last_review?.state === 'disapproved' && (
        <RequestedReview description={address?.last_review?.description} />
      )}

      <AddressForm
        addressForm={addressForm}
        disableFields={disabled}
        errors={errors.address_attributes as FieldErrors<AddressValidationSchema>}
        markFieldsAsRequired
      />
    </div>
  )
}
